import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField,
} from '@mui/material';
import { useToast } from '../../context/ToastContext';
import { AccountService } from '../../services/account.service';
import { InstitutionService } from '../../services/institution.service';
import { FixedAssetValueService } from '../../services/fixedAssetValue.service';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import { postingAccounts } from '../../utils/accountHierarchy';
import { Account } from '../../interfaces/accounting.interface';
import { RevaluationMethod } from '../../interfaces/fixedAsset/fixedAssetValue.interface';
import { toLocalIsoDate } from '../../utils/dateUtils';

export type ValueAction = 'REVALUE' | 'IMPAIR' | 'REVERSE_IMPAIRMENT' | 'RESERVE_TRANSFER';

interface Props {
  open: boolean;
  action: ValueAction;
  assetId: string;
  institutionId?: string;
  onClose: () => void;
  onDone: () => void;
}

const TITLES: Record<ValueAction, string> = {
  REVALUE: 'Revalue asset',
  IMPAIR: 'Impair asset',
  REVERSE_IMPAIRMENT: 'Reverse impairment',
  RESERVE_TRANSFER: 'Transfer revaluation reserve',
};

/** First day of the latest financial year that has ended, given the institution's first month (1-12). */
export const latestEndedFiscalYearStart = (startMonth: number, now: Date = new Date()): string => {
  const m = startMonth >= 1 && startMonth <= 12 ? startMonth : 1;
  const currentStartYear = now.getMonth() + 1 >= m ? now.getFullYear() : now.getFullYear() - 1;
  return `${currentStartYear - 1}-${String(m).padStart(2, '0')}-01`;
};

// Month end of the previous month — events need depreciation posted through exactly the event month.
const lastMonthEnd = () => {
  const d = new Date();
  return toLocalIsoDate(new Date(d.getFullYear(), d.getMonth(), 0));
};

const AssetValueActionDialog: React.FC<Props> = ({ open, action, assetId, institutionId, onClose, onDone }) => {
  const toast = useToast();
  const [date, setDate] = useState(lastMonthEnd());
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<RevaluationMethod>('ELIMINATE_ACCUMULATED');
  const [valuer, setValuer] = useState('');
  const [reportReference, setReportReference] = useState('');
  const [reason, setReason] = useState('');
  const [fiscalYearStart, setFiscalYearStart] = useState(latestEndedFiscalYearStart(1));
  const [retainedEarningsAccountId, setRetainedEarningsAccountId] = useState('');
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || action !== 'RESERVE_TRANSFER') return;
    AccountService.getLookup()
      .then(res => setAccounts(res ?? []))
      .catch(e => toast.showApiError(e, 'Failed to load accounts'));
    if (institutionId) {   // default to the latest ended year of THIS institution's financial calendar
      InstitutionService.getInstitutionById(institutionId)
        .then(inst => setFiscalYearStart(latestEndedFiscalYearStart(inst?.fiscalYearStartMonth)))
        .catch(() => undefined);
    }
  }, [open, action, institutionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const equity = postingAccounts(accounts).filter(a => a.accountType === 'EQUITY');
  const num = parseFloat(amount);
  const valid = action === 'RESERVE_TRANSFER'
    ? !!fiscalYearStart && !!retainedEarningsAccountId
    : !!date && !isNaN(num) && num >= 0 && (action === 'REVALUE' || reason.trim().length > 0)
      && (action !== 'REVALUE' || num > 0);

  const submit = async () => {
    try {
      setSaving(true);
      if (action === 'REVALUE') {
        await FixedAssetValueService.revalue(assetId, {
          date, fairValue: num, method, valuer: valuer.trim() || undefined,
          reportReference: reportReference.trim() || undefined, reason: reason.trim() || undefined,
        });
      } else if (action === 'IMPAIR') {
        await FixedAssetValueService.impair(assetId, { date, recoverableAmount: num, reason: reason.trim() });
      } else if (action === 'REVERSE_IMPAIRMENT') {
        await FixedAssetValueService.reverseImpairment(assetId, { date, amount: num, reason: reason.trim() });
      } else {
        await FixedAssetValueService.transferReserve(assetId, { fiscalYearStart, retainedEarningsAccountId });
      }
      toast.showSuccess(`${TITLES[action]} posted`);
      onDone();
    } catch (e) {
      toast.showApiError(e, `Failed: ${TITLES[action].toLowerCase()}`);
    } finally {
      setSaving(false);
    }
  };

  const amountLabel = action === 'REVALUE' ? 'Fair value' : action === 'IMPAIR' ? 'Recoverable amount' : 'Amount to reverse';

  return (
    <Dialog data-help-page="fixedAssets.valueAction.page" open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{TITLES[action]}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {action !== 'RESERVE_TRANSFER' && (
            <>
              <Alert severity="info">
                The date must be a month end, with depreciation posted through that month.
              </Alert>
              <TextField data-help="fixedAssets.valueAction.field.date" label="Event date" type="date" value={date} onChange={e => setDate(e.target.value)}
                InputLabelProps={{ shrink: true }} />
              <TextField data-help="fixedAssets.valueAction.field.amount" label={amountLabel} type="number" value={amount} onChange={e => setAmount(e.target.value)} />
            </>
          )}
          {action === 'REVALUE' && (
            <>
              <TextField data-help="fixedAssets.valueAction.field.method" select label="Method" value={method} onChange={e => setMethod(e.target.value as RevaluationMethod)}>
                <MenuItem value="ELIMINATE_ACCUMULATED">Eliminate accumulated depreciation</MenuItem>
                <MenuItem value="RESTATE_PROPORTIONALLY">Restate proportionally</MenuItem>
              </TextField>
              <TextField data-help="fixedAssets.valueAction.field.valuer" label="Valuer" value={valuer} onChange={e => setValuer(e.target.value)} />
              <TextField data-help="fixedAssets.valueAction.field.report" label="Valuation report reference" value={reportReference}
                onChange={e => setReportReference(e.target.value)} />
            </>
          )}
          {action !== 'RESERVE_TRANSFER' && (
            <TextField data-help="fixedAssets.valueAction.field.reason" label="Reason" value={reason} onChange={e => setReason(e.target.value)} multiline minRows={2}
              required={action !== 'REVALUE'} />
          )}
          {action === 'RESERVE_TRANSFER' && (
            <>
              <Alert severity="info">
                Moves the excess depreciation for the financial year from the revaluation reserve to retained
                earnings. Once per asset and year.
              </Alert>
              <TextField data-help="fixedAssets.valueAction.field.fyStart" label="Financial year start" type="date" value={fiscalYearStart}
                onChange={e => setFiscalYearStart(e.target.value)} InputLabelProps={{ shrink: true }} />
              <AccountAutocomplete data-help="fixedAssets.valueAction.field.retainedEarnings" accounts={equity} value={retainedEarningsAccountId}
                onChange={setRetainedEarningsAccountId} label="Retained earnings account" placeholder="Select" />
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button data-help="fixedAssets.valueAction.cancel" onClick={onClose}>Cancel</Button>
        <Button data-help="fixedAssets.valueAction.save" variant="contained" onClick={submit} disabled={!valid || saving}>Post</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssetValueActionDialog;
